import React, { useEffect, useRef, useState } from 'react';
import SourcesLoading, { SourcesProgress } from './SourcesLoading';
import { api } from '../api/apiConfig';
import { getConnection, prepareConnection, activateConnection, importCampaigns, disconnectConnection, campaignErrorMessage } from '../api/adpilotApi';

const authorizationErrors = {
  session_expired: 'This authorization session expired or was already used. Start again with Authorize Universal Ads.',
  declined: 'Universal Ads authorization was declined. Select Authorize Universal Ads when you are ready to connect.',
  provider_error: 'Universal Ads returned an error after authorization. Try authorizing again. If this continues, contact the AdPilot team.',
  verification_failed: 'We could not verify your Universal Ads access. Try authorizing again in a moment.',
  organization_access_required: 'Your Universal Ads organization needs to be linked to this AdPilot workspace. Contact the AdPilot team to finish setup.',
};

export default function UniversalAdsConnectCard({ user }) {
  const [connection, setConnection] = useState(null), [accounts, setAccounts] = useState([]), [campaigns, setCampaigns] = useState([]);
  const [draft, setDraft] = useState(null);
  const [showAccountPicker, setShowAccountPicker] = useState(false);
  const [loading, setLoading] = useState(!user.preview);
  const refreshSequence = useRef(0);
  const [busy, setBusy] = useState(false), [error, setError] = useState(() => {
    const reason = new URLSearchParams(window.location.search).get('universal_ads_error');
    return Object.hasOwn(authorizationErrors, reason) ? authorizationErrors[reason] : '';
  }), [message, setMessage] = useState('');
  const owner = !connection || connection.access_role === 'owner';
  const refresh = async () => {
    const sequence = ++refreshSequence.current;
    setLoading(true);
    try {
      const { connection: current } = await getConnection({ userId: user.id, provider: 'universal-ads' });
      if (sequence !== refreshSequence.current) return;
      setConnection(current);
      setAccounts([]); setCampaigns([]); setDraft(null);
      if (!current) return;
      if (current.authorized) {
        const { data } = await api.get('/api/v3/adpilot/universal-ads/accounts', { params: { connectionId: current.connection_id } });
        if (sequence !== refreshSequence.current) return;
        setAccounts(data.ad_accounts);
      }
      if (current.status === 'active') {
        const { data } = await api.get(`/api/v3/adpilot/universal-ads/${current.connection_id}/campaigns`);
        if (sequence !== refreshSequence.current) return;
        setCampaigns(data.campaigns);
        const draftId = new URLSearchParams(window.location.search).get('draftId');
        if (draftId) {
          const result = await api.get(`/api/v3/adpilot/universal-ads/${current.connection_id}/drafts/${encodeURIComponent(draftId)}`);
          if (sequence !== refreshSequence.current) return;
          setDraft(result.data.draft);
        }
      }
    } finally {
      if (sequence === refreshSequence.current) setLoading(false);
    }
  };
  useEffect(() => {
    if (user.preview) return undefined;
    let cancelled = false;
    refresh().catch((err) => { if (!cancelled && err.response?.status !== 404) setError(campaignErrorMessage(err)); });
    return () => { cancelled = true; refreshSequence.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, user.preview]);
  const run = async (action) => { setBusy(true); setError(''); setMessage(''); try { await action(); } catch (err) { setError(campaignErrorMessage(err)); } finally { setBusy(false); } };
  if (user.preview) return null;
  if (loading) return <SourcesLoading platform="Universal Ads" />;
  const connected = Boolean(connection?.authorized);
  const active = connection?.status === 'active';
  const selectedAccount = accounts.find((account) => account.ad_account_id === connection?.ad_account_id);
  const authorize = () => run(async () => {
    const result = await prepareConnection({ userId: user.id, provider: 'universal-ads' });
    if (result.authorization_url) window.location.assign(result.authorization_url); else await refresh();
  });
  return <div className="universal-ads-connect">
    {busy && <SourcesProgress platform="Universal Ads" updating />}
    {error && <div className="notice notice-error" role="alert">{error}</div>}
    {message && <div className="notice" role="status">{message}</div>}

    <div className="connection-summary" aria-label="Connection status">
      <span className="summary-pill">{active ? 'Universal Ads connected' : connected ? 'Universal Ads authorized' : 'Universal Ads not connected'}</span>
      {active && <span className="summary-pill">{selectedAccount?.name || connection.ad_account_id}</span>}
      {active && owner && <button type="button" className="summary-action" disabled={busy} onClick={() => setShowAccountPicker((value) => !value)}>Switch ad account</button>}
      {connected && owner && <button type="button" className="summary-action" disabled={busy} onClick={authorize}>Refresh connection</button>}
      {connected && owner && <button type="button" className="summary-action" disabled={busy} onClick={() => run(async () => {
        await disconnectConnection({ userId: user.id, connectionId: connection.connection_id, provider: 'universal-ads' });
        setConnection(null); setAccounts([]); setCampaigns([]); setDraft(null); setShowAccountPicker(false);
      })}>Disconnect</button>}
    </div>

    {!connected && <section className="card" aria-label="Universal Ads connection">
      <p className="eyebrow">Universal Ads</p>
      <h2 className="card-title">Connect your Universal Ads account</h2>
      <p className="card-copy">Authorize your organization, then choose the ad account you want AdPilot to watch.</p>
      <div className="campaign-actions">
        {owner && <button type="button" className="btn btn-primary" disabled={busy} onClick={authorize}>Authorize Universal Ads</button>}
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => run(refresh)}>Check authorization</button>
      </div>
    </section>}

    {connected && (!active || showAccountPicker) && <section className="card" aria-label="Ad account">
      <p className="eyebrow">Ad account</p>
      <h2 className="card-title">{active ? 'Switch Universal Ads ad account' : 'Which account should AdPilot watch?'}</h2>
      <p className="card-copy">Choose an account. You can return to Sources whenever you need to update it.</p>
      <div className="account-list">
        {accounts.length === 0 && !error && <p className="card-copy">No ad accounts are available. Check your organization authorization and try again.</p>}
        {accounts.map((account) => <button key={account.ad_account_id} type="button" className="account-button" disabled={busy || !owner} onClick={() => run(async () => {
          await activateConnection({ userId: user.id, connectionId: connection.connection_id, adAccountId: account.ad_account_id });
          await refresh(); setShowAccountPicker(false);
        })}>
          <span><strong>{account.name || 'Universal Ads ad account'}</strong><small>{account.ad_account_id}</small></span>
          <span>{account.ad_account_id === connection?.ad_account_id ? 'Current' : 'Choose →'}</span>
        </button>)}
      </div>
      <div className="campaign-actions">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => run(refresh)}>Check authorization</button>
        {active && <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setShowAccountPicker(false)}>Cancel</button>}
      </div>
    </section>}

    {active && !showAccountPicker && <>
      <section className="card" aria-label="Universal Ads campaigns">
        <div className="section-heading">
          <div><h2>Choose your campaigns</h2><span>Choose the campaigns you want AdPilot to watch.</span></div>
          <a className="btn btn-secondary" href="/create?channel=universal-ads">Create a campaign</a>
        </div>
        {campaigns.length === 0 && !error && <p className="card-copy">No campaigns were returned by Universal Ads.</p>}
        <div className="synced-list">{campaigns.map((campaign) => <div className="synced-row" key={campaign.platform_campaign_id}>
          <strong>{campaign.name}</strong><span>{campaign.status}</span>
          <button type="button" className="text-button" disabled={busy} onClick={() => run(async () => {
            await importCampaigns({ userId: user.id, connectionId: connection.connection_id, campaignIds: [campaign.platform_campaign_id] });
            setMessage(`${campaign.name} is now watched. Open Performance to review delivery.`);
          })}>Watch campaign</button>
        </div>)}</div>
      </section>
    </>}

    {draft && <section className="card" aria-label="Saved Universal Ads draft"><h2 className="card-title">Review saved plan</h2>
      <p className="card-copy">Account: {draft.adaccount_id} · Status: {draft.status}</p>
      <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(draft.draft, null, 2)}</pre>
      {draft.result && <pre>{JSON.stringify(draft.result, null, 2)}</pre>}
      <p className="card-copy">Return to the conversation to approve or decline this exact plan.</p>
    </section>}
  </div>;
}
